import {
  withCompInfo,
  createComponentMapperModel,
  withStateRefsValues,
} from '@wix/editor-elements-integrations';
import {
  getMigratedStyleProps,
  withSiteDirection,
} from '@wix/editor-elements-common-utils';
import type { CompInfo, Spx } from '@wix/editor-elements-types/thunderbolt';
import type {
  LinkBarDefinition,
  LinkBarCarmiProps,
  LinkBarCSSVars,
  LinkBarStateValues,
  LinkBarMapperPropsNoStyle,
} from '../LinkBar.types';
import * as translations from './constants';

export const props = withCompInfo<
  LinkBarMapperPropsNoStyle,
  LinkBarDefinition,
  LinkBarCarmiProps
>()(
  [
    'compProps',
    'compData',
    'experiments',
    'compId',
    'fullNameCompType',
    'language',
    'translate',
    'trackClicksAnalytics',
  ],
  (
    { compProps, compData, experiments, translate, ...compPropsRest },
    carmiData,
  ) => {
    const { iconSize } = compProps;
    return {
      ...carmiData,
      ...compPropsRest,
      iconSize,
      direction: compData.direction,
      shouldRenderPlaceholders:
        experiments['specs.thunderbolt.LinkBarPlaceholderImages'] === true,
      shouldFixFirefoxIntrinsicSizing:
        experiments['specs.thunderbolt.fixFirefoxLinkBarIntrinsicSizing'] ===
        true,
      translations: {
        ariaLabel:
          translate(
            translations.ARIA_LABEL_NAMESPACE,
            translations.ARIA_LABEL_KEY,
          ) || translations.ARIA_LABEL_DEFAULT,
      },
    };
  },
);

const stateRefs = withStateRefsValues<keyof LinkBarStateValues>(['reportBi']);

const calculateMobileSize = (
  numOfItems: number,
  iconSize: number,
  spacing: number,
  isHorizontal: boolean,
  totalItemSize: number,
) => {
  if (numOfItems === 0) {
    return { height: `${iconSize}px`, width: '5px' };
  }

  const itemSize = iconSize + spacing;
  const actualTotalSize = totalItemSize > 300 ? 300 : totalItemSize;
  const width = isHorizontal ? actualTotalSize : iconSize;
  const height = isHorizontal
    ? Math.ceil(numOfItems / Math.floor((width + spacing) / itemSize)) *
      iconSize
    : totalItemSize;
  return { width: `${width}px`, height: `${height}px` };
};

const calculateDesktopSize = (
  numOfItems: number,
  iconSize: number,
  isHorizontal: boolean,
  totalItemSize: number,
) => {
  if (numOfItems === 0) {
    return { height: `${iconSize}px`, width: `${iconSize}px` };
  }

  return {
    width: `${isHorizontal ? totalItemSize : iconSize}px`,
    height: `${isHorizontal ? iconSize : totalItemSize}px`,
  };
};

const calculateResponsiveSize = (
  numOfItems: number,
  isHorizontal: boolean,
  totalItemSize: string,
  singleItemSize: string,
) => {
  return numOfItems > 0 && !isHorizontal
    ? { height: totalItemSize, width: singleItemSize }
    : { height: singleItemSize, width: totalItemSize };
};

const calculateSize = ({
  hasResponsiveLayout,
  isMobileView,
  sizeProperties,
  compData,
  formatCssValue,
  spx,
}: {
  hasResponsiveLayout: boolean;
  isMobileView: boolean;
  sizeProperties: {
    iconSize: string;
    spacing: string;
    orientation: LinkBarDefinition['property']['orientation'];
  };
  compData: LinkBarDefinition['data'];
  formatCssValue?: CompInfo['formatCssValue'];
  spx?: Spx;
}): { width?: string; height?: string } => {
  const { iconSize, spacing, orientation } = sizeProperties;
  const numericIconSize = parseFloat(iconSize as string);
  const numericSpacing = parseFloat(spacing as string);
  const numOfItems = compData.items?.length ?? 0;
  const totalItemSize =
    numOfItems * (numericIconSize + numericSpacing) - numericSpacing;
  const singleItemSizeCssVal = formatCssValue
    ? `${formatCssValue(iconSize, spx)}`
    : iconSize;
  const totalItemSizeCssVal = formatCssValue
    ? `calc(${numOfItems} * (${formatCssValue(
        iconSize,
        spx,
      )} + ${formatCssValue(spacing, spx)}) - ${formatCssValue(spacing, spx)})`
    : totalItemSize;
  const isHorizontal = orientation === 'HORIZ';
  if (hasResponsiveLayout) {
    return calculateResponsiveSize(
      numOfItems,
      isHorizontal,
      String(totalItemSizeCssVal),
      String(singleItemSizeCssVal),
    );
  }
  if (isMobileView) {
    return calculateMobileSize(
      numOfItems,
      numericIconSize,
      numericSpacing,
      isHorizontal,
      totalItemSize,
    );
  }
  return calculateDesktopSize(
    numOfItems,
    numericIconSize,
    isHorizontal,
    totalItemSize,
  );
};

export const css = withCompInfo<LinkBarCSSVars, LinkBarDefinition>()(
  [
    'hasResponsiveLayout',
    'compProps',
    'compData',
    'isMobileView',
    'styleProperties',
    'formatCssValue',
  ],
  ({
    hasResponsiveLayout,
    compProps,
    compData,
    isMobileView,
    styleProperties,
    formatCssValue,
  }) => {
    const { spx } = styleProperties;
    const { direction = 'inherit' } = compData;

    const { orientation, iconSize, spacing } = getMigratedStyleProps({
      compProps,
      styleProperties,
      migratedFields: ['orientation', 'iconSize', 'spacing'],
    });
    const isHorizontal = orientation === 'HORIZ';
    const sizeProperties = {
      orientation,
      iconSize,
      spacing,
    };
    const getItemMargin = () => {
      if (!isHorizontal) {
        return {
          '--item-margin-block': formatCssValue(`0 ${spacing}`, spx),
          '--item-margin-inline': '0',
        };
      }

      return {
        '--item-margin-block': '0',
        '--item-margin-inline': formatCssValue(`0 ${spacing}`, spx),
      };
    };
    return {
      '--item-size': formatCssValue(iconSize, spx),
      ...getItemMargin(),
      '--item-display': isHorizontal ? 'inline-block' : 'block',
      ...(isHorizontal && {
        '--direction': withSiteDirection(direction),
      }),
      ...(hasResponsiveLayout && {
        '--flex-direction': isHorizontal ? 'row' : 'column',
      }),
      ...calculateSize({
        hasResponsiveLayout,
        isMobileView,
        sizeProperties,
        compData,
        formatCssValue,
        spx,
      }),
    };
  },
);

export default createComponentMapperModel({ props, stateRefs, css });
